import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignUp as amplifyConfirmSignUp,
  confirmSignIn as amplifyConfirmSignIn,
  type SignInInput,
  type SignUpInput,
} from "aws-amplify/auth";
import { setApiAuth } from "../api/client";

interface AuthUser {
  userId: string;
  email: string;
  givenName: string;
  familyName: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ nextStep: "DONE" | "TOTP_REQUIRED" }>;
  signUp: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  confirmSignIn: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  getJwt: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getJwt = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const loadUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const tokenEmail =
        session.tokens?.idToken?.payload?.email as string | undefined;
      let attrs: Record<string, string | undefined> = {};
      try {
        attrs = await fetchUserAttributes();
      } catch {
        // Attributes may not be available immediately after sign-up; tolerate.
      }
      return {
        userId: currentUser.userId,
        email: attrs.email || tokenEmail || "",
        givenName: attrs.given_name || "",
        familyName: attrs.family_name || "",
      };
    } catch {
      return null;
    }
  }, []);

  // Wire up the API client to use our JWT getter
  useEffect(() => {
    setApiAuth(getJwt);
  }, [getJwt]);

  // Check for existing session on mount
  useEffect(() => {
    (async () => {
      const u = await loadUser();
      setUser(u);
      setIsLoading(false);
    })();
  }, [loadUser]);

  // Email captured between signIn and confirmSignIn for the TOTP flow.
  // Used as a fallback if loadUser() can't surface an email immediately
  // after confirmSignIn resolves (rare timing edge — would otherwise
  // leave user=null and trigger a redirect loop).
  const pendingTotpEmailRef = useRef<string | null>(null);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{ nextStep: "DONE" | "TOTP_REQUIRED" }> => {
      const input: SignInInput = { username: email, password };
      const result = await amplifySignIn(input);
      if (
        !result.isSignedIn &&
        result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
      ) {
        pendingTotpEmailRef.current = email;
        return { nextStep: "TOTP_REQUIRED" };
      }
      pendingTotpEmailRef.current = null;
      const u = await loadUser();
      // Fall back to the sign-in email if Cognito attributes/tokens didn't
      // surface one yet (e.g. immediately post-sign-up before attributes
      // propagate).
      if (u) setUser({ ...u, email: u.email || email });
      else setUser({ userId: "", email, givenName: "", familyName: "" });
      return { nextStep: "DONE" };
    },
    [loadUser],
  );

  const confirmSignInFn = useCallback(async (code: string) => {
    await amplifyConfirmSignIn({ challengeResponse: code });
    const fallbackEmail = pendingTotpEmailRef.current;
    pendingTotpEmailRef.current = null;
    const u = await loadUser();
    if (u) {
      setUser({ ...u, email: u.email || fallbackEmail || "" });
    } else if (fallbackEmail) {
      setUser({ userId: "", email: fallbackEmail, givenName: "", familyName: "" });
    } else {
      setUser(null);
    }
  }, [loadUser]);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{ needsConfirmation: boolean }> => {
      const input: SignUpInput = {
        username: email,
        password,
        options: { userAttributes: { email } },
      };
      const result = await amplifySignUp(input);
      return {
        needsConfirmation: !result.isSignUpComplete,
      };
    },
    [],
  );

  const confirmSignUpFn = useCallback(
    async (email: string, code: string) => {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
    },
    [],
  );

  const signOutFn = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await loadUser();
    setUser(u);
  }, [loadUser]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    signIn,
    signUp,
    confirmSignUp: confirmSignUpFn,
    confirmSignIn: confirmSignInFn,
    signOut: signOutFn,
    getJwt,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
